import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { formatAmount } from '../../utils/formatters';
import { parseDate, toDateString } from '../../utils/dateUtils';
import BillIcon from '../common/BillIcon';
import {

  Calendar as CalendarIcon,
  Check,
  Clock,
  AlertCircle,
  Zap,
  ChevronDown,
  ChevronUp,
  Plus,
  DollarSign
} from 'lucide-react';
import '../../styles/Calendar.css';

// Constants
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// Helper functions
const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

const isToday = (date) => {
  const t = new Date();
  return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
};

/**
 * AgendaView - A reusable agenda/list view component for displaying bills
 * 
 * Props:
 * - bills: Array of bill objects
 * - payments: Array of payment objects  
 * - categories: Array of category objects
 * - onBillClick: (bill, date) => void - Called when a bill is clicked
 * - onAddClick: (date) => void - Called when add button is clicked
 * - onPaydayClick: (payday) => void - Called when a payday is clicked
 * - currentDate: Date - The current month being viewed
 * - onDateChange: (date) => void - Called when month changes
 */
function AgendaView({ 
  bills = [], 
  payments = [], 
  categories = [],
  onBillClick,
  onAddClick,
  onPaydayClick,
  currentDate,
  onDateChange,
  incomeCalcs = null,
  allExpanded = false
}) {
  const [expandedWeeks, setExpandedWeeks] = useState({});

  // Refs
  const weekRefs = useRef({});
  const sidebarRef = useRef(null);
  const mainRef = useRef(null);

  // ===========================================
  // HELPERS
  // ===========================================

  const isBillPaid = useCallback((billId, date) => {
    const target = parseDate(toDateString(date))?.getTime();
    if (!target) return false;
    const early = 14 * 24 * 60 * 60 * 1000;
    const late = 7 * 24 * 60 * 60 * 1000;
    return payments.some(p => {
      if (p.bill_id !== billId) return false;
      const pt = parseDate(p.payment_date)?.getTime();
      return pt && pt >= target - early && pt <= target + late;
    });
  }, [payments]);

  const getCategoryInfo = useCallback((name) => {
    const c = categories.find(x => x.name === name);
    return c ? { icon: c.icon } : { icon: '📋' };
  }, [categories]);

  // ===========================================
  // BILL CALCULATION
  // ===========================================

  const getBillsForDate = useMemo(() => (date) => {
    if (!date) return [];
    const t = new Date(date);
    const dow = t.getDay(), dom = t.getDate(), m = t.getMonth(), y = t.getFullYear();

    return bills.filter(bill => {
      if (!bill.is_active) return false;
      
      const start = parseDate(bill.start_date);
      if (!start || t < start) return false;
      if (bill.end_date) {
        const end = parseDate(bill.end_date);
        if (end && t > end) return false;
      }

      const freq = bill.billing_frequency;
      const due = bill.due_day;

      switch (freq) {
        case 'weekly': return dow === due;
        case 'biweekly': {
          const sw = Math.floor(start.getTime() / 604800000);
          const tw = Math.floor(t.getTime() / 604800000);
          return dow === due && (tw - sw) % 2 === 0;
        }
        case 'monthly': {
          const dim = getDaysInMonth(y, m);
          return dom === (due > dim ? dim : due);
        }
        case 'quarterly': {
          const md = (y - start.getFullYear()) * 12 + (m - start.getMonth());
          if (md < 0 || md % 3 !== 0) return false;
          const dim = getDaysInMonth(y, m);
          return dom === (due > dim ? dim : due);
        }
        case 'annually': {
          if (m !== start.getMonth()) return false;
          const dim = getDaysInMonth(y, m);
          const sd = start.getDate();
          return dom === (sd > dim ? dim : sd);
        }
        case 'one-time':
          return t.getFullYear() === start.getFullYear() && t.getMonth() === start.getMonth() && t.getDate() === start.getDate();
        default: {
          const match = freq?.match(/^(\d+)-(weekly|monthly)$/);
          if (match) {
            const int = parseInt(match[1]);
            if (match[2] === 'weekly') {
              const sw = Math.floor(start.getTime() / 604800000);
              const tw = Math.floor(t.getTime() / 604800000);
              return dow === due && (tw - sw) % int === 0;
            } else {
              const md = (y - start.getFullYear()) * 12 + (m - start.getMonth());
              return md >= 0 && md % int === 0 && dom === due;
            }
          }
          return false;
        }
      }
    });
  }, [bills]);

  // ===========================================
  // CALENDAR DATA (for mini calendar)
  // ===========================================

  const calendarData = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const dim = getDaysInMonth(y, m);
    const first = getFirstDayOfMonth(y, m);
    const prevDim = getDaysInMonth(y, m - 1);

    const days = [];

    for (let i = first - 1; i >= 0; i--) {
      const d = prevDim - i;
      const date = new Date(y, m - 1, d);
      days.push({ date, day: d, current: false, bills: getBillsForDate(date) });
    }

    for (let d = 1; d <= dim; d++) {
      const date = new Date(y, m, d);
      days.push({ date, day: d, current: true, bills: getBillsForDate(date) });
    }

    const rem = days.length % 7;
    if (rem > 0) {
      for (let d = 1; d <= 7 - rem; d++) {
        const date = new Date(y, m + 1, d);
        days.push({ date, day: d, current: false, bills: getBillsForDate(date) });
      }
    }

    return days;
  }, [currentDate, getBillsForDate]);

  // ===========================================
  // AGENDA DATA
  // ===========================================

  const agendaWeeks = useMemo(() => {
    const weeks = [];
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();

    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);

    const ws = new Date(first);
    ws.setDate(ws.getDate() - ws.getDay());

    const we = new Date(last);
    we.setDate(we.getDate() + (6 - we.getDay()));

    // Get paydays for the entire view range
    const paydays = incomeCalcs?.getPaydaysInRange ? incomeCalcs.getPaydaysInRange(ws, we) : [];
    const paydayMap = {};
    paydays.forEach(p => {
      if (!paydayMap[p.date]) paydayMap[p.date] = [];
      paydayMap[p.date].push(p);
    });

    let cur = new Date(ws);
    let idx = 0;

    while (cur <= we) {
      const end = new Date(cur);
      end.setDate(end.getDate() + 6);

      const week = { idx, start: new Date(cur), end, items: [], total: 0, overdue: 0, paid: 0, billCount: 0 };

      for (let d = new Date(cur); d <= end; d.setDate(d.getDate() + 1)) {
        const dd = new Date(d);
        const dateStr = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`;
        
        // Add paydays for this day as items
        if (paydayMap[dateStr]) {
          paydayMap[dateStr].forEach(payday => {
            week.items.push({ 
              type: 'payday',
              date: new Date(dd),
              payday: {
                dateObj: new Date(dd),
                income_name: payday.income_name,
                amount: payday.amount,
                frequency: payday.frequency,
                id: payday.id,
                icon_url: payday.icon_url
              }
            });
          });
        }
        
        // Add bills for this day as items
        getBillsForDate(dd).forEach(bill => {
          const isPaid = isBillPaid(bill.id, dd);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const isOver = !isPaid && dd < today;
          week.items.push({ 
            type: 'bill',
            date: new Date(dd), 
            bill, 
            isPaid, 
            isOver 
          });
          week.total += parseFloat(bill.amount) || 0;
          week.billCount++;
          if (isPaid) week.paid++;
          if (isOver) week.overdue++;
        });
      }

      // Sort all items by date
      week.items.sort((a, b) => a.date.getTime() - b.date.getTime());

      if (week.items.length > 0) weeks.push(week);
      cur.setDate(cur.getDate() + 7);
      idx++;
    }

    return weeks;
  }, [currentDate, getBillsForDate, isBillPaid, incomeCalcs]);

  // ===========================================
  // HANDLERS
  // ===========================================

  const handleBillClick = (bill, date, e) => { 
    e.stopPropagation(); 
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isPaid = isBillPaid(bill.id, date);
    const isOverdue = !isPaid && date < today;
    
    // Find associated payment if paid
    let payment = null;
    if (isPaid) {
      const target = parseDate(toDateString(date))?.getTime();
      if (target) {
        const early = 14 * 24 * 60 * 60 * 1000;
        const late = 7 * 24 * 60 * 60 * 1000;
        payment = payments.find(p => {
          if (p.bill_id !== bill.id) return false;
          const pt = parseDate(p.payment_date)?.getTime();
          return pt && pt >= target - early && pt <= target + late;
        });
      }
    }
    
    const billWithDueInfo = {
      ...bill,
      dueInfo: {
        dueDate: toDateString(date),
        isPaid,
        isOverdue
      }
    };
    if (onBillClick) onBillClick(billWithDueInfo, date, payment);
  };

  const scrollToWeek = (date) => {
    for (const w of agendaWeeks) {
      if (date >= w.start && date <= w.end) {
        weekRefs.current[w.idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      }
    }
  };

  // ===========================================
  // STICKY SIDEBAR
  // ===========================================

  useEffect(() => {
    const onScroll = () => {
      const sb = sidebarRef.current;
      const mn = mainRef.current;
      if (!sb || !mn) return;
      const mr = mn.getBoundingClientRect();
      const sh = sb.offsetHeight;
      if (mr.top < 24) {
        const scroll = 24 - mr.top;
        const max = Math.max(0, mr.height - sh);
        sb.style.transform = `translateY(${Math.min(scroll, max)}px)`;
      } else {
        sb.style.transform = '';
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ===========================================
  // DERIVED
  // ===========================================

  const formatRange = (s, e) => {
    const sm = s.toLocaleDateString('en-US', { month: 'short' });
    const em = e.toLocaleDateString('en-US', { month: 'short' });
    return sm === em ? `${sm} ${s.getDate()}-${e.getDate()}` : `${sm} ${s.getDate()} - ${em} ${e.getDate()}`;
  };

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <div className="agenda-view-wrapper calendar-page">
      {/* Agenda View */}
      <div className="agenda-wrap">
        <div className="agenda-main" ref={mainRef}>
          {agendaWeeks.length === 0 ? (
            <div className="agenda-empty">
              <CalendarIcon size={48} />
              <h3>No Bills This Month</h3>
              <p>Enjoy the break!</p>
            </div>
          ) : (
            agendaWeeks.map(w => {
              const lim = 5;
              const hasMore = w.items.length > lim;
              const exp = allExpanded || expandedWeeks[w.idx];
              const items = exp || !hasMore ? w.items : w.items.slice(0, lim);

              return (
                <div key={w.idx} className="agenda-week" ref={el => weekRefs.current[w.idx] = el}>
                  <div className="week-head">
                    <div className="week-info">
                      <div className="week-title">
                        <Clock size={16} />
                        <span>Week of {formatRange(w.start, w.end)}</span>
                        {w.overdue > 0 && <span className="tag danger">{w.overdue} overdue</span>}
                        {w.paid > 0 && <span className="tag success">{w.paid} paid</span>}
                      </div>
                      <div className="week-summary">
                        <span className="week-total">{formatAmount(w.total)}</span>
                        <span className="week-count">{w.billCount} bill{w.billCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    {onAddClick && <button className="icon-btn primary" onClick={() => onAddClick(w.start)}><Plus size={16} /></button>}
                  </div>

                  <div className="week-bills">
                    {/* Render items (paydays and bills) in date order */}
                    {items.map((it, j) => {
                      if (it.type === 'payday') {
                        const payday = it.payday;
                        return (
                          <div 
                            key={`payday-${j}`} 
                            className={`agenda-row payday ${onPaydayClick ? 'clickable' : ''}`}
                            onClick={() => onPaydayClick && onPaydayClick(payday)}
                          >
                            <div className="agenda-date">
                              <span className="a-day">{payday.dateObj.getDate()}</span>
                              <span className="a-dow">{payday.dateObj.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            </div>
                            <div className="payday-icon">
                              {payday.icon_url ? (
                                <img src={payday.icon_url} alt="" className="payday-logo" />
                              ) : (
                                <DollarSign size={20} />
                              )}
                            </div>
                            <div className="agenda-info">
                              <div className="a-name payday-name">{payday.income_name}</div>
                              <div className="a-meta">Payday</div>
                            </div>
                            <div className="agenda-right">
                              <span className="a-amt payday-amount">+{formatAmount(payday.amount)}</span>
                            </div>
                          </div>
                        );
                      } else {
                        // Bill item
                        const cat = getCategoryInfo(it.bill.category);
                        return (
                          <div key={`bill-${j}`} className={`agenda-row ${it.isPaid ? 'paid' : ''} ${it.isOver ? 'overdue' : ''}`} onClick={(e) => handleBillClick(it.bill, it.date, e)}>
                            <div className="agenda-date">
                              <span className="a-day">{it.date.getDate()}</span>
                              <span className="a-dow">{it.date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            </div>
                            <BillIcon iconUrl={it.bill.icon_url} categoryIcon={cat.icon} billName={it.bill.bill_name} size="sm" />
                            <div className="agenda-info">
                              <div className="a-name" style={it.bill.accent_color ? { color: it.bill.accent_color } : undefined}>
                                {it.bill.bill_name}{it.bill.auto_pay && <Zap size={12} />}
                              </div>
                              <div className="a-meta">{it.bill.category}</div>
                            </div>
                            <div className="agenda-right">
                              <span className={`a-amt ${it.isPaid ? 'paid' : ''}`}>{formatAmount(it.bill.amount)}</span>
                              {it.isPaid && <span className="badge paid"><Check size={10} /> Paid</span>}
                              {it.isOver && <span className="badge overdue"><AlertCircle size={10} /> Overdue</span>}
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>

                  {hasMore && (
                    <button className="week-expand" onClick={() => setExpandedWeeks(p => ({ ...p, [w.idx]: !p[w.idx] }))}>
                      {exp ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Show {w.items.length - lim} more</>}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="agenda-side" ref={sidebarRef}>
          <div className="mini-cal">
            <h4>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h4>
            <div className="mini-weekdays">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={i}>{d}</span>)}
            </div>
            <div className="mini-days">
              {calendarData.map((d, i) => {
                const has = d.current && d.bills.length > 0;
                const over = d.current && d.bills.some(b => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  return !isBillPaid(b.id, d.date) && d.date < today;
                });
                return (
                  <span
                    key={i}
                    className={`mini-day ${!d.current ? 'other' : ''} ${isToday(d.date) ? 'today' : ''} ${has ? 'has-bill' : ''} ${over ? 'overdue' : ''}`}
                    onClick={() => d.current && scrollToWeek(d.date)}
                  >{d.day}</span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgendaView;
